import { Switch } from '@/components/ui/switch';
import { useJob } from '@/hooks/useJobs';

/**
 * The "Cash invoice" toggle, in ONE place because both creation dialogs show
 * it — the partial dialog always did, and the final dialog now does too.
 *
 * Cash is a PER-INVOICE fact, not a job-wide one. A job billed through
 * QuickBooks can still take one payment in cash, which is exactly why the
 * partial dialog owns a switch rather than reading the job. The job's own
 * `invoiceType` seeds the switch (see useJobCashDefault) and nothing more: it
 * is the office's usual answer for this job, never a lock.
 */
export interface CashInvoiceFieldProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * What the switch opens on: the job's `invoiceType`.
 *
 * Returns `undefined` while the job is still loading, which is NOT the same as
 * `false` — the dialogs hold the user's choice as `boolean | null` and fall
 * back to this, so a default arriving a tick after mount seeds an untouched
 * switch without ever overwriting a switch the user already flipped.
 *
 * The extra query is free in practice: every caller is already on a page that
 * has fetched this job, so React Query serves it from cache.
 */
export function useJobCashDefault(jobId: string | undefined): boolean | undefined {
  const { data: job } = useJob(jobId);
  return job ? job.invoiceType === 'cash' : undefined;
}

export function CashInvoiceField({ id, checked, onChange, disabled }: CashInvoiceFieldProps) {
  return (
    <label
      htmlFor={id}
      className="flex items-center justify-between rounded-md border border-input px-3 py-2.5"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">Cash invoice</p>
        {/* Says what the switch DOES, because its two effects are invisible
            until the PDF is open: the job's cash count instead of its regular
            one, and a different payee line. */}
        <p className="text-xs text-gray-500">
          Numbered on the job&apos;s own cash count (…-C1) and printed with the cash payee.
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-label="Cash invoice"
      />
    </label>
  );
}
